import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Typography, Divider, Paper, Grid, Box, Tooltip, Button
} from '@mui/material';
import { useSnackbar } from 'notistack';
import useConfirm from 'core/hooks/useConfirm';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import CheckIcon from '@mui/icons-material/Check';
import { getLibraryBookingsPendingRefund, approveBookingRefund } from 'services/bookings';
import { tConvert } from 'utils/common';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadBlob } from 'components/reactdata-grid';

const defaultFilterValue = [
  { name: 'booking', operator: 'contains', type: 'string', value: '', fn: ({ filterValue, data }) => {
      if(filterValue === null){
        return true;
      };
      const { bookingId, fromDate, fromTime, seatIds } = data || {};

      return (
        bookingId?.toLowerCase()?.includes(filterValue.toLowerCase())
        || bookingId?.toLowerCase()?.includes(filterValue.toLowerCase())
        || fromDate?.toLowerCase()?.includes(filterValue.toLowerCase())
        || tConvert(fromTime)?.toLowerCase()?.includes(filterValue.toLowerCase())
        || (seatIds || []).join(', ').toLowerCase()?.includes(filterValue.toLowerCase())
      );
    }
  },
  { name: 'payment', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
    if(filterValue === null){
      return true;
    };
    const { transactionId, paymentChannel } = data?.bookingTransaction || {};
    return (transactionId?.toLowerCase()?.includes(filterValue.toLowerCase())
      || paymentChannel.toLowerCase()?.includes(filterValue.toLowerCase())
    );
  }},
  { name: 'lib_deatils', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
    if(filterValue === null){
      return true;
    };
    const { address, name } = data?.library || {};
    const addressArr= [];
    addressArr.push(address?.area);
    addressArr.push(address?.city);
    addressArr.push(address?.state);
    addressArr.push(address?.country);
    addressArr.push(address?.pin);
    const addressStr = (addressArr.filter((ele)=> !!ele ).join(', ') || ''); 

    return (addressStr.toLowerCase().includes(filterValue.toLowerCase())
      || name.toLowerCase().includes(filterValue.toLowerCase())
    );
  }},
  { name: 'cust', operator: 'contains', type: 'string', value: '', fn: ({ value, filterValue, data }) => {
    if(filterValue === null){
      return true;
    };
    const { displayName, mobile, email } = data?.user || {};
    return displayName?.toLowerCase()?.includes(filterValue.toLowerCase()) || mobile?.toLowerCase()?.includes(filterValue.toLowerCase()) || email?.toLowerCase()?.includes(filterValue.toLowerCase());
  }}
];

const PendingRefundPage = () => {
  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }), []);
  const [gridRef, setGridRef] = useState(null);
  const [pendingRefunds, setPendingRefunds] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const getLabelAndValueFormat = (label, value) => {
    return (
      <Box>
        <Typography variant="subtitle2" component="span">{label}: </Typography>
        <Typography variant="body2" component="span">
        {value}
        </Typography>
      </Box>
    )
  }

  const columns = useMemo(() => ([
    { name: 'booking', header: 'Booking Details', width: 300, render: ({data} ) => {
      return (
      <>
        {data?.bookingId ? getLabelAndValueFormat('Booking No', data?.bookingId): null}
        {data?.fromTime ? getLabelAndValueFormat('Timing', tConvert(data?.fromTime)): null}
        {data?.fromDate ? getLabelAndValueFormat('Date', data?.fromDate): null}
        {data?.seatCount ? getLabelAndValueFormat('Total Seats', data?.seatCount): null}
        {data?.seatIds?.length > 0 ? getLabelAndValueFormat('Seat No', data?.seatIds.join(',')): null}
      </>);
    }},
    { name: 'payment', header: 'Payment', width: 260, render: ({data} ) => {
      return (
        <>
          {data?.bookingTransaction?.paymentChannel ? getLabelAndValueFormat('Type', data?.bookingTransaction?.paymentChannel): null}
          {data?.bookingTransaction?.transactionId ? getLabelAndValueFormat('TxnId', data?.bookingTransaction?.transactionId): null}
        </>
      )
    }},
    { name: 'lib_deatils', header: 'Library Details', width: 250, render: ({data} ) => data?.library?.name || ''},
    { name: 'cust', header: 'Customer Details', width: 200, render: ({data} ) => { 
      return (
        <>
          {data?.user?.userId ? getLabelAndValueFormat('CustId', data?.user?.userId): null}
          {data?.user?.firstName ? getLabelAndValueFormat('Name', data?.user?.firstName): null}
          {data?.user?.mobile ? getLabelAndValueFormat('Mobile', data?.user?.mobile): null}
        </>
      ); 
    }},
    { name: 'amount', header: 'Amount', width: 120, render: ({data}) => data?.bookingTransaction?.amount},
    { name: 'createdOn', header: 'Created On', width: 120 },
    { name: 'actions', header: 'Actions', width: 150, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Appprove Pending Refund" arrow placement="left">
              <Button variant="contained" sx={{textTransform: 'none'}} onClick={() => onApproveBtnClick(data?.libraryId)} endIcon={<CheckIcon/>}>
                Approve Refund
              </Button>
            </Tooltip>
          </Box>
         
        </Stack>
      )
    }},
  ]),[]);

  useEffect(() => {
    getPendingApprovalData();
  }, []);

  function onApproveBtnClick(libraryId) {
    confirm({ title: 'Delete Library?', description: 'This library will be deleted and this cannot be undone. Are you sure you want to proceed?' })
    .then(() => { 
      approveRefund({libraryId});
    })
    .catch(() => { 
      
    });
  }

  function approveRefund(data) {
    approveBookingRefund(data).then(({response, error}) => {
      if(response){
        enqueueSnackbar((response?.message || 'Refund approved successfully'), {variant: 'success'});
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function getPendingApprovalData() {
    getLibraryBookingsPendingRefund().then(({response, error}) => {
      if(response){
        setPendingRefunds([...(response || [])]);
      } else {
        setPendingRefunds([]);
        // enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const reactDataGridExportCSV = (gridRef, fileName) => {
    const SEPARATOR = ',';
    // const columns = gridRef.current.visibleColumns;
  
    const header = ['Booking No', 'Booking Date', 'Time', 'Total Seats', 'Seats Nos', 'Payment Mode', 'Transaction Id', 'Library', 'Customer Name', 'Customer Mobile No', 'Amount', 'Creation Date' ].join(SEPARATOR);
    const rows = gridRef.current.data.map((data) => {
      const rowValue = [];
      rowValue.push(data?.bookingId || '');
      rowValue.push(data?.fromDate || '');
      rowValue.push(tConvert(data?.fromTime) || '');
      rowValue.push(data?.seatCount || 0);
      rowValue.push(`"${(data?.seatIds || []).join(', ')}"` || '');
      rowValue.push(data?.bookingTransaction?.paymentChannel || '');
      rowValue.push(data?.bookingTransaction?.transactionId || '');
      rowValue.push(data?.library?.name || '');
      rowValue.push(data?.user?.firstName || '');
      rowValue.push(data?.user?.mobile || '');
      rowValue.push(data?.bookingTransaction?.amount || '');
      rowValue.push(data?.createdOn || '');
      return rowValue.join(SEPARATOR);
    });
    const contents = [header].concat(rows).join('\n');
    const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, fileName);
  };

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Pending Refunds
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12} textAlign="right" >
            <Button onClick={() => reactDataGridExportCSV(gridRef, 'pending-refunds.csv') } startIcon={<DownloadIcon />} variant="outlined" color="success" sx={{textTransform: 'none'}}>Export CSV</Button>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{ maxHeight: 720, width: '100%' }}>
                <ReactDataGrid
                  style={gridStyle}
                  defaultFilterValue={defaultFilterValue}
                  columns={columns}
                  dataSource={pendingRefunds}
                  rowHeight={null} 
                  minRowHeight={50}
                />

            </Box>
          </Grid>
        </Grid>
      </Box> 
    </Paper>
  );
}
 
export default React.memo(PendingRefundPage);
